import React, { useState, useMemo, useEffect } from 'react';
import { Table, Tag, Collapse, Pagination, Button, message, Modal, Divider, Checkbox, Space } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import api, { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';
import { getDataFormsSimple } from '../utils/dataForms';
import { formatDateTime } from '../utils/timeUtils';
import { groupSubmissionsByBatch } from '../utils/submissionUtils';
import EditableDataTable from './EditableDataTable';

const DataTable = ({ 
  dataType, 
  submissions, 
  fields, 
  onRefresh, 
  user, 
  showAll, 
  departmentId, 
  disableBatchGrouping,
  // 可选：由外层控制的行选择配置（用于审核时的按行复选）
  rowSelection,
  // 是否显示再编辑按钮（普通用户历史记录中）
  showReEditButton = false,
  // 博士授权点审核：表格行列对换（行为数据项，列为每条提交记录）
  transposeReviewTable = false
}) => {
  const [batchPage, setBatchPage] = useState(1); // 批次分页
  const [batchPageSize, setBatchPageSize] = useState(10); // 批次每页数量
  const [departments, setDepartments] = useState([]);
  const [isEditingMode, setIsEditingMode] = useState(false); // 是否处于编辑模式
  const [editingSubmissions, setEditingSubmissions] = useState([]); // 要编辑的记录

  // 加载部门列表
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await api.get('/departments');
        setDepartments(response.data || []);
      } catch (error) {
        console.error('加载部门列表失败:', error);
      }
    };
    loadDepartments();
  }, []);

  // 根据数据类型和部门ID获取中文标签
  const getDataTypeLabel = (dataType, deptId) => {
    if (!deptId) return dataType;
    const dept = departments.find(d => d.id === deptId);
    if (!dept) return dataType;
    const dataForms = getDataFormsSimple(dept.name);
    const formConfig = dataForms.find(f => f.key === dataType);
    return formConfig ? formConfig.label : dataType;
  };

  // 将提交记录按批次分组
  // 批次定义：同一部门、同一数据类型、同一提交时间（精确到秒）、同一提交者、同一状态的数据为一批次
  // 对于已审核的记录（approved/rejected），审核时间（reviewed_at）也作为批次key的一部分，确保不同时间审核的记录分开显示
  // 注意：状态也作为批次key的一部分，确保不同状态的记录分开显示
  // 通用排序函数（支持数字、日期、字符串）
  const getSorter = (key, fieldType) => {
    return (a, b) => {
      let aVal = a.data_content?.[key];
      let bVal = b.data_content?.[key];
      
      // 处理空值
      if (aVal === null || aVal === undefined || aVal === '') aVal = null;
      if (bVal === null || bVal === undefined || bVal === '') bVal = null;
      
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      
      // 根据字段类型排序
      if (fieldType === 'number' || fieldType === 'count') {
        const aNum = parseFloat(aVal) || 0;
        const bNum = parseFloat(bVal) || 0;
        return aNum - bNum;
      } else if (fieldType === 'date') {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        return aDate - bDate;
      } else {
        // 字符串排序
        return String(aVal).localeCompare(String(bVal), 'zh-CN');
      }
    };
  };

  // 构建表格列
  const buildColumns = () => {
    // 批量数据不需要显示提交时间和提交者
    const baseColumns = [];

    if (showAll) {
      baseColumns.unshift({
        title: '数据类型',
        dataIndex: 'data_type',
        key: 'data_type',
        width: 150,
        render: (text, record) => getDataTypeLabel(text, record.department_id),
        sorter: (a, b) => {
          const aLabel = getDataTypeLabel(a.data_type, a.department_id);
          const bLabel = getDataTypeLabel(b.data_type, b.department_id);
          return aLabel.localeCompare(bLabel, 'zh-CN');
        }
      });
    }

    // 添加数据字段列（按照字段定义的顺序，与数据录入时保持一致）
    if (fields && submissions.length > 0) {
      const firstSubmission = submissions[0];
      const dataContent = firstSubmission.data_content || {};
      
      // 按照字段定义的顺序创建列（与数据录入时保持一致，包含computed字段）
      fields.forEach(field => {
        if (field.computed) {
          // computed字段：实时计算并显示（通常不排序，但可以按计算值排序）
          baseColumns.push({
            title: field.label,
            key: field.key,
            width: 150,
            render: (_, record) => {
              if (field.formula && record.data_content) {
                const computedValue = field.formula(record.data_content);
                return computedValue !== undefined && computedValue !== null && computedValue !== '' 
                  ? String(computedValue) 
                  : '-';
              }
              // 如果data_content中已经有计算好的值，直接显示
              return record.data_content?.[field.key] ?? '-';
            },
            sorter: (a, b) => {
              const aVal = field.formula ? (field.formula(a.data_content) || 0) : (a.data_content?.[field.key] || 0);
              const bVal = field.formula ? (field.formula(b.data_content) || 0) : (b.data_content?.[field.key] || 0);
              return parseFloat(aVal) - parseFloat(bVal);
            }
          });
        } else {
          // 非computed字段：直接显示data_content中的值，支持排序
          baseColumns.push({
            title: field.label,
            key: field.key,
            width: 150,
            render: (_, record) => {
              return record.data_content?.[field.key] ?? '-';
            },
            sorter: getSorter(field.key, field.type)
          });
        }
      });
      
      // 对于dataContent中存在但字段定义中不存在的字段，也显示（兼容性，追加到末尾）
      // 排除系统字段：isDraft, isNew, _replaced_by 等
      // 以及内部使用的审核字段：_rejection_reason, _rejection_detail
      const systemFields = new Set(['isDraft', 'isNew', '_replaced_by', '_rejection_reason', '_rejection_detail', 'key', 'id', 'status']);
      const definedKeys = new Set(fields.map(f => f.key));
      Object.keys(dataContent).forEach(key => {
        if (!definedKeys.has(key) && !systemFields.has(key)) {
          // 尝试推断字段类型
          const sampleValue = dataContent[key];
          let fieldType = 'string';
          if (typeof sampleValue === 'number') {
            fieldType = 'number';
          } else if (sampleValue && typeof sampleValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(sampleValue)) {
            fieldType = 'date';
          }
          
          baseColumns.push({
            title: key,
            key: key,
            width: 150,
            render: (_, record) => {
              return record.data_content?.[key] ?? '-';
            },
            sorter: getSorter(key, fieldType)
          });
        }
      });
    } else if (submissions.length > 0) {
      // 如果没有fields，动态生成列（按对象键顺序）
      // 排除系统字段：isDraft, isNew, _replaced_by 等
      // 以及内部使用的审核字段：_rejection_reason, _rejection_detail
      const systemFields = new Set(['isDraft', 'isNew', '_replaced_by', '_rejection_reason', '_rejection_detail', 'key', 'id', 'status']);
      const firstSubmission = submissions[0];
      const dataContent = firstSubmission.data_content || {};
      Object.keys(dataContent).forEach(key => {
        if (!systemFields.has(key)) {
          // 尝试推断字段类型
          const sampleValue = dataContent[key];
          let fieldType = 'string';
          if (typeof sampleValue === 'number') {
            fieldType = 'number';
          } else if (sampleValue && typeof sampleValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(sampleValue)) {
            fieldType = 'date';
          }
          
          baseColumns.push({
            title: key,
            key: key,
            width: 150,
            render: (_, record) => {
              return record.data_content?.[key] ?? '-';
            },
            sorter: getSorter(key, fieldType)
          });
        }
      });
    }

    return baseColumns;
  };

  // 处理数据，保持data_content结构
  // 历史记录显示所有状态的记录；有被拒绝记录时显示“再编辑”按钮
  const processedData = submissions.map(sub => ({
    ...sub,
    data_content: sub.data_content || {}
  }));
  
  // 检查是否有被拒绝的记录
  const hasRejectedRecords = showReEditButton && user && user.role === 'user' && 
    processedData.some(s => s.status === 'rejected');

  const getFieldDisplayForSubmission = (field, sub) => {
    const dc = sub.data_content || {};
    if (field.computed && field.formula) {
      try {
        const v = field.formula(dc);
        return v !== undefined && v !== null && v !== '' ? String(v) : '-';
      } catch (e) {
        return '-';
      }
    }
    const v = dc[field.key];
    if (v === null || v === undefined || v === '') return '-';
    if (field.type === 'date' && v) {
      try {
        return String(v).slice(0, 10);
      } catch (e) {
        return String(v);
      }
    }
    if (field.type === 'daterange' && Array.isArray(v)) {
      return `${v[0] || ''} ~ ${v[1] || ''}`;
    }
    return String(v);
  };

  // 按批次分组显示（如果禁用批次分组，直接显示表格）
  const batches = useMemo(() => {
    if (disableBatchGrouping) {
      return null; // 禁用批次分组
    }
    return groupSubmissionsByBatch(processedData);
  }, [processedData, disableBatchGrouping]);

  // 批次分页处理
  const paginatedBatches = useMemo(() => {
    if (batches && batches.length > 0) {
    const start = (batchPage - 1) * batchPageSize;
    const end = start + batchPageSize;
    return batches.slice(start, end);
    }
    return [];
  }, [batches, batchPage, batchPageSize]);

  return (
    <div>
      {disableBatchGrouping ? (
        // 禁用批次分组，直接显示表格 + 下方审核信息
        processedData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            <p>暂无数据</p>
          </div>
        ) : (
          <>
            {/* 如果是普通用户历史记录且有被拒绝的记录，显示再编辑按钮 */}
            {hasRejectedRecords && !isEditingMode && (
              <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingSubmissions(processedData.filter(s => s.status === 'rejected'));
                    setIsEditingMode(true);
                  }}
                >
                  再编辑
                </Button>
              </div>
            )}
            
            {isEditingMode ? (
              // 编辑模式：使用EditableDataTable
              <EditableDataTable
                dataType={dataType}
                fields={fields}
                submissions={editingSubmissions}
                onRefresh={() => {
                  if (onRefresh) {
                    onRefresh();
                  }
                  setIsEditingMode(false);
                  setEditingSubmissions([]);
                }}
                user={user}
                departmentId={departmentId}
              />
            ) : transposeReviewTable && fields && fields.length > 0 && processedData.length > 0 ? (
              (() => {
                const subs = [...processedData].sort((a, b) => (a.id || 0) - (b.id || 0));
                const selKeys = rowSelection?.selectedRowKeys || [];
                const onSelChange = rowSelection?.onChange;
                const cols = [
                  {
                    title: '数据项',
                    dataIndex: 'rowLabel',
                    key: 'rowLabel',
                    fixed: 'left',
                    width: 220,
                    render: (text, record) => (
                      <span style={{ fontWeight: record._meta ? 500 : 'normal' }}>{text}</span>
                    )
                  },
                  ...subs.map((sub, idx) => ({
                    title: (
                      <div style={{ textAlign: 'center', minWidth: 100 }}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          {rowSelection && sub.status === 'pending' && (
                            <Checkbox
                              checked={selKeys.includes(sub.id)}
                              onChange={(e) => {
                                if (!onSelChange) return;
                                const next = e.target.checked
                                  ? [...selKeys, sub.id]
                                  : selKeys.filter((k) => k !== sub.id);
                                onSelChange(next);
                              }}
                            />
                          )}
                          <div>第 {idx + 1} 条</div>
                          <div style={{ fontSize: 11, color: '#666' }}>
                            ID:{sub.id}
                          </div>
                          {sub.data_content?.年份 != null && sub.data_content?.年份 !== '' && (
                            <Tag>{sub.data_content.年份}年</Tag>
                          )}
                          <Tag color={sub.status === 'approved' ? 'green' : sub.status === 'rejected' ? 'red' : 'orange'}>
                            {sub.status === 'approved' ? '已通过' : sub.status === 'rejected' ? '已拒绝' : '待审核'}
                          </Tag>
                        </Space>
                      </div>
                    ),
                    key: `col-${sub.id}`,
                    align: 'center',
                    width: 140,
                    render: (_, record) => record.cells[sub.id] ?? '-'
                  }))
                ];
                const rows = [];
                rows.push({
                  key: 'meta-id',
                  rowLabel: '记录编号',
                  _meta: true,
                  cells: Object.fromEntries(subs.map((s) => [s.id, String(s.id)]))
                });
                rows.push({
                  key: 'meta-time',
                  rowLabel: '提交时间',
                  _meta: true,
                  cells: Object.fromEntries(
                    subs.map((s) => [s.id, s.submitted_at ? formatDateTime(s.submitted_at) : '-'])
                  )
                });
                fields.forEach((field) => {
                  rows.push({
                    key: `f-${field.key}`,
                    rowLabel: field.label.startsWith('其中:') ? '  ' + field.label : field.label,
                    cells: Object.fromEntries(
                      subs.map((s) => [s.id, getFieldDisplayForSubmission(field, s)])
                    )
                  });
                });
                return (
                  <Table
                    columns={cols}
                    dataSource={rows}
                    rowKey="key"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    size="middle"
                    bordered
                  />
                );
              })()
            ) : (
              // 查看模式：使用普通Table（只在审核时显示复选框）
              <Table
                columns={buildColumns()}
                dataSource={processedData}
                rowKey="id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                bordered
                rowSelection={rowSelection ? {
                  ...rowSelection,
                  getCheckboxProps: (record) => ({
                    disabled: record.status === 'approved' || record.status === 'rejected',
                    ...(rowSelection.getCheckboxProps ? rowSelection.getCheckboxProps(record) : {})
                  })
                } : undefined}
              />
            )}
            {/* 在表格下方展示当前批次的证明材料（管理员审核等场景） */}
            {(() => {
              // 汇总该表中所有记录的证明材料（去重）
              const proofMap = new Map();
              processedData.forEach(sub => {
                const list = sub.proof_materials || [];
                list.forEach(pm => {
                  if (pm && pm.id && !proofMap.has(pm.id)) {
                    proofMap.set(pm.id, pm);
                  }
                });
              });
              const proofs = Array.from(proofMap.values());
              if (!proofs.length) return null;

              const apiUrl = getApiBaseURL();
              const token = getToken();
              const handleDownload = async (materialId, filename) => {
                try {
                  if (!token) {
                    message.error('请先登录');
                    return;
                  }
                  const response = await fetch(`${apiUrl}/proof-materials/${materialId}/download`, {
                    method: 'GET',
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  if (!response.ok) {
                    let errorMessage = '下载失败';
                    try {
                      const error = await response.json();
                      errorMessage = error.error || errorMessage;
                    } catch (e) {
                      errorMessage = response.statusText || errorMessage;
                    }
                    if (response.status === 401 || response.status === 422) {
                      errorMessage = '登录已过期，请重新登录';
                    }
                    throw new Error(errorMessage);
                  }
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename || '证明材料';
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (error) {
                  console.error('下载证明材料失败:', error);
                  message.error(error.message || '下载证明材料失败');
                }
              };

              const columns = [
                {
                  title: '序号',
                  dataIndex: 'index',
                  key: 'index',
                  width: 70,
                  render: (_text, _record, index) => index + 1
                },
                {
                  title: '文件名',
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (text, record) => (
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(record.id, record.filename);
                      }}
                    >
                      {text}
                    </a>
                  )
                },
                {
                  title: '备注',
                  dataIndex: 'remark',
                  key: 'remark',
                  render: (text) => text || '-'
                }
              ];

              return (
                <div style={{ marginTop: 16 }}>
                  <Divider orientation="left" plain>
                    证明材料
                  </Divider>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    columns={columns}
                    dataSource={proofs}
                    bordered
                  />
                </div>
              );
            })()}

            {/* 在表格下方集中展示所有被拒绝记录的审核信息（邮件格式） */}
            {(() => {
              const rejected = processedData.filter(
                d => d.status === 'rejected' && (d.rejection_reason || d.rejection_detail)
              );
              if (rejected.length === 0) return null;

              // 按 (reason, detail, reviewed_by, reviewed_at) 组合分组
              const summaryMap = new Map();
              rejected.forEach(d => {
                const reason = d.rejection_reason || '无';
                const detail = d.rejection_detail || '';
                const reviewedBy = d.reviewed_by_username || '管理员';
                const reviewedAt = d.reviewed_at || '';
                const key = `${reason}||${detail}||${reviewedBy}||${reviewedAt}`;
                const prev = summaryMap.get(key) || { 
                  count: 0, 
                  reason, 
                  detail, 
                  reviewedBy, 
                  reviewedAt,
                  recordIds: []
                };
                prev.count += 1;
                prev.recordIds.push(d.id);
                summaryMap.set(key, prev);
              });

              const summaries = Array.from(summaryMap.values());

              return (
                <div style={{ marginTop: 16 }}>
                  <div style={{ 
                    background: '#fff', 
                    border: '1px solid #d9d9d9', 
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ 
                      padding: '16px 20px', 
                      background: '#fafafa', 
                      borderBottom: '1px solid #e8e8e8',
                      borderTopLeftRadius: 4,
                      borderTopRightRadius: 4
                    }}>
                      <div style={{ 
                        fontSize: 16, 
                        fontWeight: 'bold', 
                        color: '#fa541c',
                        marginBottom: 4
                      }}>
                        📧 审核通知
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        共 {rejected.length} 条记录被拒绝
                      </div>
                    </div>
                    
                    {summaries.map((item, idx) => (
                      <div key={idx} style={{ 
                        padding: '16px 20px',
                        borderBottom: idx < summaries.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: 8
                          }}>
                            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#333' }}>
                              拒绝原因
                            </div>
                            <Tag color="red" style={{ margin: 0 }}>
                              {item.count} 条记录
                            </Tag>
                          </div>
                          <div style={{ 
                            padding: '12px',
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: 4,
                            marginBottom: 12
                          }}>
                            <div style={{ 
                              fontSize: 13, 
                              color: '#333',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {item.reason.split('；').map((r, i) => (
                                <div key={i} style={{ marginBottom: i < item.reason.split('；').length - 1 ? 4 : 0 }}>
                                  • {r}
                                </div>
                              ))}
                            </div>
                            {item.detail && (
                              <>
                                <Divider style={{ margin: '12px 0' }} />
                                <div style={{ 
                                  fontSize: 13, 
                                  color: '#666',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {item.detail}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: 12,
                          color: '#999',
                          paddingTop: 8,
                          borderTop: '1px solid #f0f0f0'
                        }}>
                          <div>
                            <span style={{ marginRight: 16 }}>
                              <strong>审核人：</strong>{item.reviewedBy}
                            </span>
                            {item.reviewedAt && (
                              <span>
                                <strong>审核时间：</strong>{formatDateTime(item.reviewedAt)}
                              </span>
                            )}
                          </div>
                          <div style={{ color: '#999' }}>
                            记录ID: {item.recordIds.join(', ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </>
        )
      ) : batches && batches.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          <p>暂无历史记录</p>
        </div>
      ) : (
        <>
          <Collapse
            defaultActiveKey={[]}
            style={{ background: '#fff' }}
            items={paginatedBatches.map(batch => {
            return {
              key: batch.key,
              label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold' }}>
                      {getDataTypeLabel(batch.data_type, batch.department_id)}
                    </span>
                    <Tag color="blue">{batch.submissions.length} 条数据</Tag>
                    {batch.status && (
                      batch.status === 'mixed' ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {batch.statusArray?.map(status => (
                            <Tag 
                              key={status}
                              color={status === 'approved' ? 'green' : status === 'rejected' ? 'red' : 'orange'}
                            >
                              {status === 'approved' ? '已通过' : status === 'rejected' ? '已拒绝' : '待审核'}
                            </Tag>
                          ))}
                        </div>
                      ) : (
                        <Tag color={batch.status === 'approved' ? 'green' : batch.status === 'rejected' ? 'red' : 'orange'}>
                          {batch.status === 'approved' ? '已通过' : batch.status === 'rejected' ? '已拒绝' : '待审核'}
                        </Tag>
                      )
                    )}
                  </div>
                </div>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      size="small"
                      onClick={async () => {
                        if (batch.submissions.length === 0) {
                          message.warning('没有数据可导出');
                          return;
                        }

                        try {
                          const params = new URLSearchParams();
                          params.append('department_id', batch.department_id);
                          params.append('data_type', batch.data_type);
                          // 导出该批次的所有记录，不限制状态
                          params.append('submission_ids', batch.ids.join(','));

                          const token = getToken();
                          if (!token) {
                            message.error('请先登录');
                            return;
                          }

                          const apiUrl = getApiBaseURL();
                          const response = await fetch(`${apiUrl}/submissions/export?${params.toString()}`, {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });

                          if (!response.ok) {
                            let errorMessage = '导出失败';
                            try {
                              const error = await response.json();
                              errorMessage = error.error || errorMessage;
                            } catch (e) {
                              errorMessage = response.statusText || errorMessage;
                            }
                            
                            if (response.status === 401 || response.status === 422) {
                              errorMessage = '登录已过期，请重新登录';
                            }
                            
                            throw new Error(errorMessage);
                          }

                          // 获取文件名
                          const contentDisposition = response.headers.get('Content-Disposition');
                          let filename = `${batch.data_type}_${new Date().getTime()}.xlsx`;
                          if (contentDisposition) {
                            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                            if (filenameMatch && filenameMatch[1]) {
                              filename = filenameMatch[1].replace(/['"]/g, '');
                              if (filename.startsWith('UTF-8\'\'')) {
                                filename = decodeURIComponent(filename.replace('UTF-8\'\'', ''));
                              }
                            }
                          }

                          // 创建Blob并下载
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = filename;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);

                          message.success('导出成功');
                        } catch (error) {
                          console.error('导出失败:', error);
                          message.error(error.message || '导出失败');
                        }
                      }}
                    >
                      导出该批次
                    </Button>
                  </div>
                  <Table
                    columns={buildColumns()}
                    dataSource={batch.submissions}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    size="middle"
                    bordered
                    rowSelection={rowSelection ? {
                      ...rowSelection,
                      getCheckboxProps: (record) => ({
                        disabled: record.status === 'approved' || record.status === 'rejected',
                        ...(rowSelection.getCheckboxProps ? rowSelection.getCheckboxProps(record) : {})
                      })
                    } : undefined}
                  />
                  {/* 证明材料列表（批次级） */}
                  {(() => {
                    // 汇总该批次所有记录的证明材料（去重）
                    const proofMap = new Map();
                    batch.submissions.forEach(sub => {
                      const list = sub.proof_materials || [];
                      list.forEach(pm => {
                        if (!proofMap.has(pm.id)) {
                          proofMap.set(pm.id, pm);
                        }
                      });
                    });
                    const proofs = Array.from(proofMap.values());
                    if (!proofs.length) return null;

                    const apiUrl = getApiBaseURL();
                    const token = getToken();
                    const handleDownload = async (materialId, filename) => {
                      try {
                        if (!token) {
                          message.error('请先登录');
                          return;
                        }
                        const response = await fetch(`${apiUrl}/proof-materials/${materialId}/download`, {
                          method: 'GET',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (!response.ok) {
                          let errorMessage = '下载失败';
                          try {
                            const error = await response.json();
                            errorMessage = error.error || errorMessage;
                          } catch (e) {
                            errorMessage = response.statusText || errorMessage;
                          }
                          if (response.status === 401 || response.status === 422) {
                            errorMessage = '登录已过期，请重新登录';
                          }
                          throw new Error(errorMessage);
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename || '证明材料';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error('下载证明材料失败:', error);
                        message.error(error.message || '下载证明材料失败');
                      }
                    };

                    return (
                      <div style={{ marginTop: 12 }}>
                        <Divider orientation="left" plain>
                          证明材料
                        </Divider>
                        {proofs.map(pm => (
                          <div key={pm.id} style={{ marginBottom: 4 }}>
                            <a
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(pm.id, pm.filename);
                              }}
                            >
                              {pm.filename}
                            </a>
                            {pm.remark && (
                              <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                                （{pm.remark}）
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* 在每个批次下方展示该批次内被拒绝记录的审核信息（邮件格式） */}
                  {/* 一个批次内只显示一次拒绝原因，不对每条数据行进行说明 */}
                  {(() => {
                    const rejected = batch.submissions.filter(
                      s => s.status === 'rejected' && (s.rejection_reason || s.rejection_detail)
                    );
                    if (rejected.length === 0) return null;

                    // 同一个批次内的所有被拒绝记录应该使用相同的拒绝原因
                    // 取第一条被拒绝记录的拒绝原因作为整个批次的拒绝原因
                    const firstRejected = rejected[0];
                    const reason = firstRejected.rejection_reason || '无';
                    const detail = firstRejected.rejection_detail || '';
                    const reviewedBy = firstRejected.reviewed_by_username || '管理员';
                    const reviewedAt = firstRejected.reviewed_at || '';
                    const recordIds = rejected.map(s => s.id);

                    return (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ 
                          background: '#fff', 
                          border: '1px solid #d9d9d9', 
                          borderRadius: 4,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ 
                            padding: '12px 16px', 
                            background: '#fafafa', 
                            borderBottom: '1px solid #e8e8e8',
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4
                          }}>
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 'bold', 
                              color: '#fa541c',
                              marginBottom: 2
                            }}>
                              📧 审核通知
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              共 {rejected.length} 条记录被拒绝
                            </div>
                          </div>
                          
                          <div style={{ 
                            padding: '12px 16px'
                          }}>
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: 6
                              }}>
                                <div style={{ fontSize: 13, fontWeight: 'bold', color: '#333' }}>
                                  拒绝原因
                                </div>
                                <Tag color="red" style={{ margin: 0, fontSize: 11 }}>
                                  {rejected.length} 条
                                </Tag>
                              </div>
                              <div style={{ 
                                padding: '10px',
                                background: '#fff7e6',
                                border: '1px solid #ffd591',
                                borderRadius: 4,
                                marginBottom: 10
                              }}>
                                <div style={{ 
                                  fontSize: 12, 
                                  color: '#333',
                                  lineHeight: '1.5',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {reason.split('；').map((r, i) => (
                                    <div key={i} style={{ marginBottom: i < reason.split('；').length - 1 ? 4 : 0 }}>
                                      • {r}
                                    </div>
                                  ))}
                                </div>
                                {detail && (
                                  <>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <div style={{ 
                                      fontSize: 12, 
                                      color: '#666',
                                      lineHeight: '1.5',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}>
                                      {detail}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              fontSize: 11,
                              color: '#999',
                              paddingTop: 6,
                              borderTop: '1px solid #f0f0f0'
                            }}>
                              <div>
                                <span style={{ marginRight: 12 }}>
                                  <strong>审核人：</strong>{reviewedBy}
                                </span>
                                {reviewedAt && (
                                  <span>
                                    <strong>审核时间：</strong>{formatDateTime(reviewedAt)}
                                  </span>
                                )}
                              </div>
                              <div style={{ color: '#999', fontSize: 10 }}>
                                ID: {recordIds.join(', ')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )
            };
          })}
          />
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              current={batchPage}
              pageSize={batchPageSize}
              total={batches.length}
              onChange={(page, pageSize) => {
                setBatchPage(page);
                setBatchPageSize(pageSize);
              }}
              onShowSizeChange={(current, size) => {
                setBatchPage(1);
                setBatchPageSize(size);
              }}
              showSizeChanger
              showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个批次`}
              pageSizeOptions={['10', '20', '50', '100']}
            />
          </div>
        </>
      )}

    </div>
  );
};

export default DataTable;

